import { useCallback, useEffect, useMemo, useState } from 'react';
import { GuaranteeDto } from 'api/pricing';
import { ConfigGarantieItemDto } from 'api/real-estate';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { usePanel } from 'hooks/usePanel';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { realEstateAPI } from 'services/swagger-api/realEstateAPI';
import { selectGlobalGuarantees, selectGlobalGuaranteesReferential } from '../slices/pricing/selectors/guaranteeFilterSelector';
import { GuaranteesReferential } from '../types/interfaces';
import { setGuaranteeLastResearchHash, updateGlobalGuarantees } from '../slices/pricing/pricingSlice';
import { useSubscriptionProcessState } from 'features/subscription-process/contexts/selectors/selectors';
import { selectAllValuesForAPI } from '../slices/pricing-values/selectors';
import { createGuaranteeLastResearchHash } from '../slices/pricing/utils/createGuaranteeLastResearchHash';
import { fetchFunnelsByGuarantees } from '../slices/pricing/thunks/fetchFunnelsByGuaranteesThunk';

interface GuaranteeValues {
  deductible?: number;
  ceiling?: number;
}

/**
 * Parc immobilier : la couverture globale est la source de vérité persistée (les devis en dérivent).
 * Quand on valide les garanties globales, on enregistre la config du parc (global mis à jour ;
 * biensInclus + overrides préservés). No-op si le risque n'est pas un parc (biensInclus vide).
 *
 * Retourne true si le re-tarif peut suivre : risque hors parc, ou sauvegarde confirmée par le back.
 * Le back tarifie un parc depuis la config persistée : re-tarifer sans attendre ce true afficherait
 * un prix calculé sur l'ancienne couverture.
 */
const persistParcGlobalGuarantees = async (riskId: string, selected: GuaranteesReferential): Promise<boolean> => {
  const showError = (message?: string | null) =>
    toasterService.showToast({
      message: message || "La couverture globale n'a pas pu être enregistrée.",
      displayType: DisplayTypeEnum.Toaster,
      color: GenericColors.Red,
    });

  try {
    const config = await realEstateAPI.getParcConfig({ riskId });
    if (!config?.biensInclus?.length) return true;

    const global: ConfigGarantieItemDto[] = [...selected.mandatory, ...selected.optional].map((g) => ({
      garantieId: g.id,
      active: true,
      franchise: g.deductible?.value,
      plafond: g.ceiling?.value,
    }));

    const response = await realEstateAPI.saveParcConfig({ riskId, parcConfigDto: { ...config, global } });
    if (!response?.isSuccess) {
      console.error('Persistance de la couverture globale du parc refusée:', response?.errorMessage);
      showError(response?.errorMessage);
      return false;
    }

    return true;
  } catch (e) {
    console.error('Persistance de la couverture globale du parc échouée:', e);
    showError();
    return false;
  }
};

enum GuaranteeFilterTab {
  Mandatory,
  Optional
}

interface UseGuaranteeFilterProps {
  isOptional: boolean;
  /** Sélection initiale (défaut : sélection globale Redux). En mode bien : l'override du bien. */
  initialGuarantees?: GuaranteesReferential;
  /** Si fourni (mode bien) : appelé au submit avec la sélection, au lieu d'écrire le global + re-tarifer. */
  onApply?: (selected: GuaranteesReferential) => void;
}

export const useGuaranteeFilter = ({ isOptional, initialGuarantees, onApply }: UseGuaranteeFilterProps) => {
  const { riskId } = useSubscriptionProcessState()

  const dispatch = useAppDispatch();
  const values = useAppSelector(selectAllValuesForAPI);

  const { globalGuaranteesReferential, hasOptionalGuarantees } = useAppSelector(selectGlobalGuaranteesReferential);
  const globalGuarantees = useAppSelector(selectGlobalGuarantees);
  const globalGuaranteesRef = useAppSelector(selectGlobalGuaranteesReferential);

  // Source de la sélection : override du bien si fourni, sinon la sélection globale.
  const source = useMemo(() => initialGuarantees ?? globalGuarantees, [initialGuarantees, globalGuarantees]);

  const [currentTab, setCurrentTab] = useState<number | string>(
    isOptional ? GuaranteeFilterTab.Optional : GuaranteeFilterTab.Mandatory
  );
  const [currentGuarantees, setCurrentGuarantees] = useState<GuaranteesReferential>(source);

  const { setPanelActions, closePanel } = usePanel();

  const currentSearchHash = useMemo(() => {
    const searchData = {
      riskId,
      guarantees: [
        ...globalGuarantees.optional.map(g => ({
          id: g.id,
          deductible: g.deductible?.value,
          ceiling: g.ceiling?.value,
        })),
        ...globalGuarantees.mandatory.map(g => ({
          id: g.id,
          deductible: g.deductible?.value,
          ceiling: g.ceiling?.value,
        }))
      ],
    };

    return createGuaranteeLastResearchHash(searchData);
  }, [globalGuarantees, riskId]);

  useEffect(() => {
    const initialValues: Record<string, GuaranteeValues> = {};

    const processGuarantees = (referentialGuarantees: GuaranteeDto[], guarantees: GuaranteeDto[]) => {
      referentialGuarantees.forEach(refGuarantee => {
        const guaranteeValue: GuaranteeValues = {};

        const guarantee = guarantees.find(g => g.id === refGuarantee.id);

        if (guarantee?.deductible?.value !== undefined) {
          guaranteeValue.deductible = guarantee.deductible.value;
        } else if (refGuarantee.deductible?.value !== undefined) {
          guaranteeValue.deductible = refGuarantee.deductible.value;
        }

        if (guarantee?.ceiling?.value !== undefined) {
          guaranteeValue.ceiling = guarantee.ceiling.value;
        } else if (refGuarantee.ceiling?.value !== undefined) {
          guaranteeValue.ceiling = refGuarantee.ceiling.value;
        }

        if (guaranteeValue.deductible !== undefined || guaranteeValue.ceiling !== undefined) {
          initialValues[refGuarantee.id] = guaranteeValue;
        }
      });
    };

    // Traiter les garanties optionnelles (valeurs franchise/plafond issues de la source)
    processGuarantees(
      globalGuaranteesRef.globalGuaranteesReferential.optional,
      source.optional
    );

    // Traiter les garanties obligatoires
    processGuarantees(
      globalGuaranteesRef.globalGuaranteesReferential.mandatory,
      source.mandatory
    );

    setGuaranteeValues(initialValues);
  }, [globalGuaranteesRef, source]);


  // Sélectionner toutes les garanties optionnelles
  const onSelectAll = useCallback(() => {
    globalGuaranteesReferential.optional.forEach(guarantee => {
      if (!currentGuarantees.optional.some((g) => g.id === guarantee.id)) {
        setCurrentGuarantees(prev => ({
          ...prev,
          optional: [...prev.optional, guarantee]
        }));
      }
    });
  }, [globalGuaranteesReferential, currentGuarantees]);

  // Désélectionner toutes les garanties optionnelles
  const onDeselectAllOptional = useCallback(() => {
    setCurrentGuarantees(prev => ({
      ...prev,
      optional: []
    }));
  }, []);

  // Calculer le nombre de garanties sélectionnées
  const selectedCount = useMemo(() => {
    if (currentTab === GuaranteeFilterTab.Mandatory) {
      return currentGuarantees.mandatory.length;
    } else {
      return currentGuarantees.optional.length;
    }
  }, [currentGuarantees, currentTab]);

  // Vérifier si une garantie est active
  const isActive = useCallback((guaranteeId: string) => {
    const allGuarantees = [
      ...currentGuarantees.optional,
      ...currentGuarantees.mandatory,
    ];
    return allGuarantees.some(g => g.id === guaranteeId);
  }, [currentGuarantees]);

  // Basculer l'état d'une garantie
  const handleToggleGuarantee = useCallback((guarantee: GuaranteeDto): void => {
    const guaranteeType = guarantee.isRequired ? 'mandatory' : 'optional';
    const currentList = currentGuarantees[guaranteeType];
    const guaranteeExists = currentList.some(g => g.id === guarantee.id);

    setCurrentGuarantees(prev => ({
      ...prev,
      [guaranteeType]: guaranteeExists
        ? currentList.filter(g => g.id !== guarantee.id)
        : [...currentList, guarantee]
    }));
  }, [currentGuarantees]);

  const [guaranteeValues, setGuaranteeValues] = useState<Record<string, GuaranteeValues>>({});

  const updateDeductible = useCallback((guaranteeId: string, value: number) => {
    setGuaranteeValues(prev => ({
      ...prev,
      [guaranteeId]: {
        ...prev[guaranteeId],
        deductible: value
      }
    }));
  }, []);

  const updateCeiling = useCallback((guaranteeId: string, value: number) => {
    setGuaranteeValues(prev => ({
      ...prev,
      [guaranteeId]: {
        ...prev[guaranteeId],
        ceiling: value
      }
    }));
  }, []);

  const getGuaranteeValue = useCallback((guaranteeId: string): GuaranteeValues => {
    return guaranteeValues[guaranteeId] || {};
  }, [guaranteeValues]);

  const handleSubmit = useCallback(async () => {
    const optionalGuarantees = currentGuarantees.optional.map((guarantee) => {
      const currentGuaranteeValues = getGuaranteeValue(guarantee.id);

      return {
        ...guarantee,
        ceiling: guarantee.ceiling ? {
          ...guarantee.ceiling,
          value: currentGuaranteeValues.ceiling != null
            ? currentGuaranteeValues.ceiling
            : guarantee.ceiling.value
        } : undefined,
        deductible: guarantee.deductible ? {
          ...guarantee.deductible,
          value: currentGuaranteeValues.deductible != null
            ? currentGuaranteeValues.deductible
            : guarantee.deductible.value
        } : undefined,
      };
    });

    const mandatoryGuarantees = currentGuarantees.mandatory.map((guarantee) => {
      const currentGuaranteeValues = getGuaranteeValue(guarantee.id);

      return {
        ...guarantee,
        ceiling: guarantee.ceiling ? {
          ...guarantee.ceiling,
          value: currentGuaranteeValues.ceiling != null
            ? currentGuaranteeValues.ceiling
            : guarantee.ceiling.value
        } : undefined,
        deductible: guarantee.deductible ? {
          ...guarantee.deductible,
          value: currentGuaranteeValues.deductible != null
            ? currentGuaranteeValues.deductible
            : guarantee.deductible.value
        } : undefined,
      };
    });

    const selected: GuaranteesReferential = {
      optional: optionalGuarantees,
      mandatory: mandatoryGuarantees,
    };

    // Mode bien : on délègue au caller (mise à jour de l'override), pas d'écriture globale ni de re-tarif.
    if (onApply) {
      onApply(selected);
      return;
    }

    dispatch(updateGlobalGuarantees(selected));

    if (!riskId) {
      closePanel();
      return;
    }

    // Parc : la couverture globale persistée est la source de vérité du tarif côté back,
    // on attend donc sa sauvegarde avant de re-tarifer. En échec, on garde le panel ouvert.
    const ok = await persistParcGlobalGuarantees(riskId, selected);
    if (!ok)
      return;

    const searchParams = {
      riskId,
      guarantees: [
        ...optionalGuarantees.map(g => ({
          id: g.id,
          deductible: g.deductible?.value,
          ceiling: g.ceiling?.value,
        })),
        ...mandatoryGuarantees.map(g => ({
          id: g.id,
          deductible: g.deductible?.value,
          ceiling: g.ceiling?.value,
        }))
      ],
      fees: values.fees,
      variables: values.variables
    };
    dispatch(setGuaranteeLastResearchHash(currentSearchHash));
    dispatch(fetchFunnelsByGuarantees(searchParams));
    closePanel();
  }, [currentGuarantees, getGuaranteeValue, dispatch, closePanel, currentSearchHash, riskId, values, onApply]);

  // Configuration des actions du panel — uniquement en mode global (panel).
  // En mode bien, le composant rend son propre pied (Retour / Valider).
  useEffect(() => {
    if (onApply) return;
    setPanelActions({
      closeLabel: "COMMON.CANCEL",
      submitLabel: "Valider",
      // handleSubmit ferme lui-même le panel en cas de succès (et le laisse ouvert en cas d'échec).
      onSubmit: async () => {
        await handleSubmit();
      }
    });
  }, [onApply, currentGuarantees, dispatch, handleSubmit, closePanel, setPanelActions]);

  return {
    // État
    currentTab,
    setCurrentTab,
    currentGuarantees,
    globalGuaranteesReferential,
    hasOptionalGuarantees,
    selectedCount,

    // Actions
    onSelectAll,
    onDeselectAllOptional,
    isActive,
    handleToggleGuarantee,

    // Constantes
    GuaranteeFilterTab,

    updateDeductible,
    updateCeiling,
    getGuaranteeValue,
    handleSubmit,
  };
};
